/**
 * Mappers for Billable Services
 *
 * Converts between Response models (from API), State models (for UI), and Request models (to API)
 */

import {
  BillableServiceResponse,
  BillableServiceChargeResponse
} from './billable-service-response.model';

import {
  CreateBillableServiceRequest,
  UpdateBillableServiceRequest,
  CreateBillableServiceChargeRequest,
  UpdateBillableServiceChargeRequest
} from './billable-service-request.model';

import {
  BillableServiceFormState,
  BillableServiceChargeFormState
} from './billable-service-state.model';

import {
  PlanServiceState,
  PlanChargeState
} from '../../subscription-plans/models/subscription-plan-state.model';

/**
 * Map Service Response to Service State (for plan builder)
 */
export function mapServiceResponseToState(service: BillableServiceResponse): PlanServiceState {
  const charges: PlanChargeState[] = service.billableServiceCharges?.map(c => ({
    chargeId: c.id,
    chargeName: c.name,
    type: c.type,
    frequency: c.frequency,
    dataType: c.valueDataType,
    originalValue: c.value,
    overriddenValue: null,
    includedSeats: null
  })) || [];

  return {
    serviceId: service.id,
    serviceName: service.name,
    category: service.category,
    appId: service.app?.id || service.appId,
    permissionTemplateId: null,
    charges,
    addons: []
  };
}

/**
 * Map Charge Response to Charge State (for plan builder)
 */
export function mapChargeResponseToState(charge: BillableServiceChargeResponse): PlanChargeState {
  return {
    chargeId: charge.id,
    chargeName: charge.name,
    type: charge.type,
    frequency: charge.frequency,
    dataType: charge.valueDataType,
    originalValue: charge.value,
    overriddenValue: null,
    includedSeats: null
  };
}

// ─── Form State Mappers ───────────────────────────────────────────────────────

/**
 * Map Service Response to Form State
 */
export function mapServiceResponseToFormState(service: BillableServiceResponse): BillableServiceFormState {
  return {
    id: service.id,
    appId: service.app?.id || service.appId,
    name: service.name,
    description: service.description,
    isSystemDefault: service.isSystemDefault,
    category: service.category,
    status: service.status,
    charges: service.billableServiceCharges?.map(mapChargeResponseToChargeFormState) || [],
    isDirty: false,
    validationErrors: {},
    showChargeForm: false,
    editingChargeIndex: null
  };
}

/**
 * Map Charge Response to Charge Form State
 */
export function mapChargeResponseToChargeFormState(charge: BillableServiceChargeResponse): BillableServiceChargeFormState {
  return {
    id: charge.id,
    name: charge.name,
    description: charge.description,
    type: charge.type,
    frequency: charge.frequency,
    value: charge.value,
    valueDataType: charge.valueDataType,
    isSystemDefault: charge.isSystemDefault,
    status: charge.status,
    isEditing: false,
    isDirty: false
  };
}

/**
 * Map Form State to Create Service Request
 */
export function mapFormStateToCreateRequest(state: BillableServiceFormState): CreateBillableServiceRequest {
  return {
    appId: state.appId,
    name: state.name,
    isSystemDefault: state.isSystemDefault,
    category: state.category,
    status: 2, // Active by default
    charges: state.charges.map(c => ({
      name: c.name,
      type: c.type,
      frequency: c.frequency,
      value: c.value,
      valueDataType: c.valueDataType,
      isSystemDefault: c.isSystemDefault,
      status: 2 // Active by default
    }))
  };
}

/**
 * Map Form State to Update Service Request
 */
export function mapFormStateToUpdateRequest(state: Partial<BillableServiceFormState>): UpdateBillableServiceRequest {
  const request: UpdateBillableServiceRequest = {};

  if (state.appId !== undefined) request.appId = state.appId;
  if (state.name !== undefined) request.name = state.name;
  if (state.isSystemDefault !== undefined) request.isSystemDefault = state.isSystemDefault;
  if (state.category !== undefined) request.category = state.category;

  if (state.charges !== undefined) {
    request.charges = state.charges.map(c => ({
      name: c.name,
      type: c.type,
      frequency: c.frequency,
      value: c.value,
      valueDataType: c.valueDataType,
      isSystemDefault: c.isSystemDefault,
      status: 2 // Active by default
    }));
  }

  return request;
}
